import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Avatar, Button, StatusDot } from '@/components';
import { formatTime } from '@/lib/format';
import type { AgentWithCounts } from '@/lib/api';
import type { ChatMessage } from '@shared/types';
import './ChatWorkspace.scss';

export interface ChatWorkspaceProps {
  agent: AgentWithCounts;
  messages: ChatMessage[];
  loading: boolean;
  sending: boolean;
  connected: boolean;
  onSend: (message: string) => void;
  onConfigure: () => void;
  onBack: () => void;
}

function statusLine(agent: AgentWithCounts, connected: boolean): string {
  const parts: string[] = [];
  if (agent.useKnowledge) {
    parts.push(`${agent.sourceCount} source${agent.sourceCount === 1 ? '' : 's'}`);
  }
  parts.push(`${agent.rules.length} rule${agent.rules.length === 1 ? '' : 's'}`);
  if (agent.useApi) parts.push(connected ? 'live account data' : 'account disconnected');
  return parts.join(' · ');
}

export const ChatWorkspace: React.FC<ChatWorkspaceProps> = ({
  agent,
  messages,
  loading,
  sending,
  connected,
  onSend,
  onConfigure,
  onBack,
}) => {
  const [input, setInput] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  // Keep the newest message in view as the thread grows or the agent replies.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, sending]);

  function submit(text: string) {
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    onSend(trimmed);
    setInput('');
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') {
      event.preventDefault();
      submit(input);
    }
  }

  const isEmpty = !loading && messages.length === 0;

  return (
    <div className="chat">
      <header className="chat__head">
        <button type="button" className="chat__back" onClick={onBack}>
          &#8592; Agents
        </button>
        <Avatar name={agent.name} colour={agent.colour} size={40} />
        <div className="chat__identity">
          <h1 className="chat__name">{agent.name}</h1>
          <div className="chat__status">
            <StatusDot tone="live" pulse />
            {statusLine(agent, connected)}
          </div>
        </div>
        <Button variant="secondary" size="sm" onClick={onConfigure}>
          Configure
        </Button>
      </header>

      <div className="chat__scroll" ref={scrollRef}>
        {loading && <div className="chat__loading">Loading conversation…</div>}

        {isEmpty && (
          <div className="chat__welcome">
            <Avatar name={agent.name} colour={agent.colour} size={64} className="chat__welcome-avatar" />
            <h2 className="chat__welcome-name">{agent.name}</h2>
            <p className="chat__welcome-purpose">{agent.purpose}</p>
            <div className="chat__suggestions">
              {agent.suggestions.map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  className="chat__suggestion"
                  onClick={() => submit(suggestion)}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((message) => (
          <div
            key={message.id}
            className={`chat__row chat__row--${message.role === 'user' ? 'user' : 'agent'}`}
          >
            <div className="chat__bubble">{message.content}</div>
            <div className="chat__meta">
              <span>{formatTime(message.createdAt)}</span>
              {message.queriedResources.length > 0 && (
                <span className="chat__queried">
                  <StatusDot tone="blue" size={5} />
                  queried {message.queriedResources.join(', ')}
                </span>
              )}
            </div>
          </div>
        ))}

        {sending && (
          <div className="chat__typing" aria-label="Agent is replying">
            <span />
            <span />
            <span />
          </div>
        )}
      </div>

      <div className="chat__composer">
        <div className="chat__input-wrap">
          <input
            className="chat__input"
            value={input}
            placeholder={`Ask ${agent.name} something…`}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
          />
          <Button size="sm" loading={sending} onClick={() => submit(input)}>
            Send
          </Button>
        </div>
        <div className="chat__footnote">
          Replies generated live · agent follows its rules and sources
        </div>
      </div>
    </div>
  );
};

export default ChatWorkspace;
